import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileUp, Upload, XCircle } from 'lucide-react';
import { Alert, Button, Card, Spinner } from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { CsvAccountImpact, CsvIngestResult } from '@pnl/sdk';
import { api } from '../lib/api';

function fmtBrl(cents: number, signed = false): string {
  const v = cents / 100;
  const formatted = v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return signed && cents > 0 ? `+${formatted}` : formatted;
}

const statusClass: Record<string, string> = {
  PENDING: 'bg-warning-50 text-warning-700',
  PROCESSING: 'bg-brand-50 text-brand-700',
  SUCCESS: 'bg-success-50 text-success-700',
  PARTIAL: 'bg-warning-50 text-warning-700',
  FAILED: 'bg-danger-50 text-danger-700',
};

const phaseActionLabel: Record<CsvAccountImpact['phaseAction'], string> = {
  NONE: 'Sem mudança',
  // Bater a meta não promove sozinho: entra em "Metas atingidas" pra validação.
  META_REACHED: '🎯 Meta atingida — aguarda validação',
  ADVANCE_TO_FUNDED: '⬆ Avança para Funded',
  ADVANCE_TO_LIVE: '⬆ Avança para Live',
  BLOW_ACCOUNT: '⛔ Drawdown estourado',
};

export function CsvImportsPage() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [referenceDate, setReferenceDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [dryRun, setDryRun] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CsvIngestResult | null>(null);

  const listQuery = useQuery({
    queryKey: ['csv-imports'],
    queryFn: () => api.listCsvImports(),
  });

  const upload = useMutation({
    mutationFn: () => api.uploadCsv(file!, referenceDate, dryRun),
    onSuccess: (res) => {
      setPreview(res);
      if (!dryRun) {
        qc.invalidateQueries({ queryKey: ['csv-imports'] });
        setFile(null);
      }
    },
    onError: (err: unknown) => {
      if (err instanceof PnlApiError) setError(err.api.message);
      else setError('Falha no upload.');
    },
  });

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
    setPreview(null);
    setError(null);
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!file) return setError('Selecione um arquivo CSV');
    if (!referenceDate) return setError('Selecione a data de referência');
    upload.mutate();
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">M3</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Importações CSV
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Suba o CSV diário da Nelogica. Use dry-run pra ver o impacto antes de aplicar.
        </p>
      </div>

      <Card>
        <form onSubmit={submit} className="space-y-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-ink-900 mb-1.5 block text-xs font-semibold">Arquivo CSV</label>
              <div className="border-ink-200 hover:border-brand-400 bg-ink-50 relative cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFile}
                  className="absolute inset-0 cursor-pointer opacity-0"
                />
                <FileUp size={20} className="text-ink-400 mx-auto" />
                <p className="text-ink-700 mt-2 text-sm font-semibold">
                  {file ? file.name : 'Clique para selecionar'}
                </p>
                {file && (
                  <p className="text-ink-500 mt-1 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-ink-900 mb-1.5 block text-xs font-semibold">
                Data de referência
              </label>
              <input
                type="date"
                value={referenceDate}
                onChange={(e) => setReferenceDate(e.target.value)}
                className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
                required
              />
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dryRun"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  className="text-brand-500 focus:ring-brand-500/20 rounded"
                />
                <label htmlFor="dryRun" className="text-ink-700 text-sm">
                  Dry-run (preview sem aplicar)
                </label>
              </div>
            </div>
          </div>

          {error && <Alert tone="danger">{error}</Alert>}

          <div className="flex gap-2">
            <Button
              type="submit"
              leadingIcon={<Upload size={14} />}
              loading={upload.isPending}
              disabled={!file}
            >
              {dryRun ? 'Pré-visualizar impacto' : 'Importar agora'}
            </Button>
            {dryRun && preview?.dryRun && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setDryRun(false);
                  upload.mutate();
                }}
                loading={upload.isPending}
              >
                Confirmar e aplicar
              </Button>
            )}
          </div>
        </form>

        {preview && (
          <div className="border-ink-100 border-t p-6">
            <div className="grid gap-4 md:grid-cols-4">
              <Stat label="Linhas no CSV" value={String(preview.rowCount)} />
              <Stat label="Trades parseados" value={String(preview.tradesParsed)} />
              <Stat
                label="Erros"
                value={String(preview.errors.length)}
                tone={preview.errors.length > 0 ? 'warning' : 'success'}
              />
              <Stat label="Subcontas afetadas" value={String(preview.uniqueAccounts.length)} />
            </div>

            {preview.errors.length > 0 && (
              <div className="border-warning-200 bg-warning-50 mt-4 rounded-lg border p-4">
                <p className="text-warning-700 mb-2 inline-flex items-center gap-2 text-sm font-semibold">
                  <AlertTriangle size={14} /> Linhas com erro
                </p>
                <ul className="text-warning-700 max-h-40 list-inside list-disc overflow-y-auto text-xs">
                  {preview.errors.slice(0, 20).map((e, idx) => (
                    <li key={idx}>
                      L{e.line}: {e.reason}
                    </li>
                  ))}
                  {preview.errors.length > 20 && (
                    <li>...e mais {preview.errors.length - 20} erro(s)</li>
                  )}
                </ul>
              </div>
            )}

            {preview.impacts.length > 0 && (
              <div className="mt-5">
                <p className="text-ink-900 mb-3 text-sm font-semibold">
                  Impacto em {preview.impacts.length} conta(s) virtual(is)
                </p>
                <div className="border-ink-200 overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-ink-50">
                      <tr className="text-2xs text-ink-500 text-left font-semibold uppercase tracking-widest">
                        <th className="px-4 py-2.5">Conta</th>
                        <th className="px-2 py-2.5 text-right">Saldo anterior</th>
                        <th className="px-2 py-2.5 text-right">PnL do dia</th>
                        <th className="px-2 py-2.5 text-right">Novo saldo</th>
                        <th className="px-2 py-2.5 text-right">DD usado</th>
                        <th className="px-4 py-2.5">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.impacts.map((i) => (
                        <tr key={i.virtualAccountId} className="border-ink-100 border-t">
                          <td className="text-ink-900 px-4 py-2 font-medium">{i.label}</td>
                          <td className="text-ink-700 px-2 py-2 text-right tabular-nums">
                            {fmtBrl(i.prevBalanceCents)}
                          </td>
                          <td
                            className={
                              'px-2 py-2 text-right font-semibold tabular-nums ' +
                              (i.deltaCents > 0
                                ? 'text-success-700'
                                : i.deltaCents < 0
                                  ? 'text-danger-700'
                                  : 'text-ink-700')
                            }
                          >
                            {fmtBrl(i.deltaCents, true)}
                          </td>
                          <td className="text-ink-900 px-2 py-2 text-right font-semibold tabular-nums">
                            {fmtBrl(i.newBalanceCents)}
                          </td>
                          <td className="text-warning-700 px-2 py-2 text-right tabular-nums">
                            {i.drawdownUsedCents > 0 ? fmtBrl(i.drawdownUsedCents) : '—'}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            <span
                              className={
                                'inline-flex items-center gap-1 ' +
                                (i.phaseAction === 'BLOW_ACCOUNT'
                                  ? 'text-danger-700'
                                  : i.phaseAction === 'ADVANCE_TO_FUNDED'
                                    ? 'text-success-700'
                                    : 'text-ink-500')
                              }
                            >
                              {i.phaseAction === 'BLOW_ACCOUNT' && <XCircle size={12} />}
                              {i.phaseAction === 'ADVANCE_TO_FUNDED' && <CheckCircle2 size={12} />}
                              {phaseActionLabel[i.phaseAction]}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!preview.dryRun && (
              <Alert tone="success" className="mt-4">
                Importação aplicada com sucesso. CsvImport ID: {preview.csvImportId}
              </Alert>
            )}
          </div>
        )}
      </Card>

      <Card>
        <header className="border-ink-100 flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-ink-900 text-base font-semibold">Histórico de importações</h2>
        </header>
        {listQuery.isLoading && (
          <div className="flex min-h-[120px] items-center justify-center">
            <Spinner size={20} />
          </div>
        )}
        {listQuery.data && listQuery.data.items.length === 0 && (
          <p className="text-ink-500 px-6 py-10 text-center text-sm">
            Nenhuma importação ainda. Suba o primeiro CSV no formulário acima.
          </p>
        )}
        {listQuery.data && listQuery.data.items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-2xs text-ink-500 text-left font-semibold uppercase tracking-widest">
                <th className="px-6 py-3">Arquivo</th>
                <th className="px-3 py-3">Data ref.</th>
                <th className="px-3 py-3">Importado em</th>
                <th className="px-3 py-3">Por</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.data.items.map((i) => (
                <tr key={i.id} className="border-ink-100 border-t">
                  <td className="text-ink-900 px-6 py-3 font-medium">{i.fileName}</td>
                  <td className="text-ink-700 px-3 py-3 tabular-nums">{i.referenceDate}</td>
                  <td className="text-ink-700 px-3 py-3 text-xs tabular-nums">
                    {new Date(i.importedAt).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-ink-700 px-3 py-3 text-xs">{i.importedBy ?? '—'}</td>
                  <td className="px-6 py-3">
                    <span
                      className={
                        'text-2xs rounded-full px-2.5 py-1 font-semibold ' +
                        (statusClass[i.status] ?? 'bg-ink-100 text-ink-700')
                      }
                    >
                      {i.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warning' | 'success';
}) {
  const color =
    tone === 'warning'
      ? 'text-warning-700'
      : tone === 'success'
        ? 'text-success-700'
        : 'text-ink-900';
  return (
    <div className="border-ink-200 rounded-lg border bg-white px-4 py-3">
      <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">{label}</p>
      <p className={'mt-1 text-xl font-bold tabular-nums ' + color}>{value}</p>
    </div>
  );
}
